'use client';

import PropTypes from 'prop-types';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';
import MarketingReveal from '@/components/website/Marketing/MarketingReveal';
import {
  MARKETING_PAIR_BLOCK_SX,
  MARKETING_PAIR_IMAGE_SX,
} from '@/components/website/Marketing/MarketingIndexChrome';
import { scrollMarketingSection } from '@/components/website/Marketing/marketing-scroll';
import {
  MARKETING,
  MARKETING_BODY_SX,
  MARKETING_HEADING_SX,
  MARKETING_SOLID_CTA_SX,
} from '@/components/website/Marketing/marketing-tokens';
import MarketingSectionImage from '@/components/website/MarketingPage/MarketingSectionImage';

function paragraphsFromDescription(description) {
  if (description == null || description === '') return [];
  if (Array.isArray(description)) return description.filter(Boolean);
  return [description];
}

function hashTargetId(href) {
  if (typeof href !== 'string' || !href.startsWith('#') || href.length < 2) return '';
  const raw = href.slice(1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Editorial trade card — image + copy pair styled like Brand/Process
 * indexed_text_image sections. Keeps DynamicCard unchanged for boutique pages.
 */
export default function TradeCard({
  id,
  title = '',
  description,
  href = '',
  urlText = '',
  image,
  imageAlt = '',
  aligned = 'left',
}) {
  const paragraphs = paragraphsFromDescription(description);
  const imageOnLeft = aligned !== 'right';
  const isExternal = typeof href === 'string' && /^https?:\/\//i.test(href);
  const hashId = hashTargetId(href);

  const handleHashClick = event => {
    event.preventDefault();
    scrollMarketingSection(hashId);
  };

  let linkProps = { component: Link, href };
  if (isExternal) {
    linkProps = { component: 'a', href, target: '_blank', rel: 'noopener noreferrer' };
  } else if (hashId) {
    linkProps = { component: 'a', href, onClick: handleHashClick };
  }

  return (
    <MarketingReveal
      component="section"
      id={id}
      animate={false}
      sx={{
        px: MARKETING.padX,
        py: { xs: '56px', md: '72px' },
        borderBottom: `1px solid ${MARKETING.hairline}`,
      }}
    >
      <Box sx={MARKETING_PAIR_BLOCK_SX}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            order: { md: imageOnLeft ? 2 : 1 },
            alignSelf: 'center',
          }}
        >
          {title ? (
            <MarketingReveal observe={false} delay={0}>
              <Typography component="h2" sx={MARKETING_HEADING_SX}>
                {title}
              </Typography>
            </MarketingReveal>
          ) : null}
          {paragraphs.map((paragraph, index) => (
            <MarketingReveal
              key={`${index}-${typeof paragraph === 'string' ? paragraph.slice(0, 24) : index}`}
              observe={false}
              delay={0.12 * (index + 1)}
            >
              <Typography
                component={typeof paragraph === 'string' ? 'p' : 'div'}
                sx={{ ...MARKETING_BODY_SX, whiteSpace: 'pre-line' }}
              >
                {paragraph}
              </Typography>
            </MarketingReveal>
          ))}
          {href && urlText ? (
            <MarketingReveal observe={false} delay={0.12 * (paragraphs.length + 1)}>
              <Box
                {...linkProps}
                sx={{ ...MARKETING_SOLID_CTA_SX, alignSelf: 'flex-start', mt: '8px' }}
              >
                {urlText}
              </Box>
            </MarketingReveal>
          ) : null}
        </Box>
        {image ? (
          <MarketingReveal
            observe={false}
            delay={0.35}
            from={imageOnLeft ? 'left' : 'right'}
            sx={{ width: '100%', order: { xs: -1, md: imageOnLeft ? 1 : 2 } }}
          >
            <MarketingSectionImage
              src={image}
              alt={imageAlt || (title ? `${title} image` : 'Trade card image')}
              sizes="(max-width: 899px) 100vw, 50vw"
              sx={MARKETING_PAIR_IMAGE_SX}
            />
          </MarketingReveal>
        ) : null}
      </Box>
    </MarketingReveal>
  );
}

TradeCard.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node])),
  ]),
  href: PropTypes.string,
  urlText: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  aligned: PropTypes.oneOf(['left', 'right']),
};
